import { z } from 'zod';

import { BUILDER_ROW_TYPES } from '../constants/campaignBuilder.js';
import { BuyingPlanInfoResponseSchema } from './buyingPlanInfo.js';

const VersionListRequestPayloadSchema = z.object({
  mediaPlanId: z.number(),
  versionName: z.string().optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
});

export const VersionLiteSchema = z.object({
  approvedAt: z.string().nullable(),
  approvedBy: z.string().nullable(),
  canBeDeleted: z.boolean(),
  canBeDuplicated: z.boolean(),
  createdAt: z.string(),
  description: z.string(),
  id: z.number(),
  isCurrent: z.boolean(),
  lastUpdate: z.string(),
  mediaPlan: z.number(),
  name: z.string(),
});

export const VersionListResponseSchema = z.object({
  lastPage: z.number(),
  versions: z.array(VersionLiteSchema),
});

const BudgetItem = z.object({
  current: z.string().nullable(),
  previous: z.string().nullable(),
  difference: z.string().nullable(),
});

const KpiItem = z
  .object({
    current: z.string().nullable(),
    previous: z.string().nullable(),
    difference: z.number().nullable(),
  })
  .or(
    z.object({
      current: z.number().nullable(),
      previous: z.number().nullable(),
      difference: z.number().nullable(),
    }),
  );

const ChannelMixItem = z.object({
  current: z.string().nullable(),
  currentPercent: z.string().nullable(),
  previous: z.string().nullable(),
  difference: z.string().nullable(),
});

export const ChannelMixVersionResponseSchema = z.object({
  budget: z.object({
    adserverCost: BudgetItem,
    dspCost: BudgetItem,
    mediaBudget: BudgetItem,
    otherTechCost: BudgetItem,
    total: z.string(),
  }),
  channelMix: z.record(z.string(), ChannelMixItem),
  dateRange: z.object({
    dateFrom: z.string().nullable(),
    dateTo: z.string().nullable(),
  }),
  description: z.string(),
  kpis: z.record(z.string(), KpiItem),
  version: z.number(),
});

export const VersionResponseSchema = z.object({
  approvedAt: z.string().nullable(),
  approvedBy: z.string().nullable(),
  createdAt: z.string(),
  description: z.string(),
  id: z.number(),
  isCurrent: z.boolean(),
  lastUpdate: z.string(),
  mediaPlanData: BuyingPlanInfoResponseSchema,
  name: z.string(),
  updatedAt: z.string(),
});

const VersionFieldsDataSchema = z.object({
  adserverCost: z.string().nullable(),
  budget: z.string().nullable(),
  calculatedClicks: z.coerce.string().nullable(),
  calculatedConversions: z.coerce.string().nullable(),
  calculatedImpressions: z.coerce.string().nullable(),
  calculatedLeads: z.coerce.string().nullable(),
  calculatedVideoViews: z.coerce.string().nullable(),
  compoundBudget: z.string().nullable(),
  cr: z.string().nullable(),
  ctr: z.string().nullable(),
  dateFrom: z.string().nullable(),
  dateTo: z.string().nullable(),
  dspCost: z.string().nullable(),
  vtr: z.string().nullable(),
});

const VersionItemSchema = z.object({
  fields: VersionFieldsDataSchema,
  label: z.string(),
  name: z.string(),

  subItems: z.array(
    z.object({
      fields: VersionFieldsDataSchema,
      label: z.string(),
      name: z.string(),
      type: z.enum(BUILDER_ROW_TYPES),
    }),
  ),
  type: z.enum(BUILDER_ROW_TYPES),
});

const VersionForTableViewSchema = z.object({
  canBeDeleted: z.boolean(),
  canBeDuplicated: z.boolean(),
  id: z.number(),
  isCurrent: z.boolean(),
  items: z.array(VersionItemSchema),
  name: z.string(),
  totals: VersionFieldsDataSchema,
});

export const VersionListForTableResponseSchema = z.object({
  fieldsMeta: z.array(z.object({ name: z.string(), label: z.string() })),
  versions: z.array(VersionForTableViewSchema),
});

const UpdateVersionPayloadSchema = z.union([
  z.object({ versionId: z.number(), name: z.string() }),
  z.object({ versionId: z.number(), description: z.string() }),
]);

export type VersionListRequestPayload = z.infer<
  typeof VersionListRequestPayloadSchema
>;
export type VersionListResponse = z.infer<typeof VersionListResponseSchema>;
export type ChannelMixVersionResponse = z.infer<
  typeof ChannelMixVersionResponseSchema
>;
export type VersionResponse = z.infer<typeof VersionResponseSchema>;
export type VersionListForTableResponse = z.infer<
  typeof VersionListForTableResponseSchema
>;
export type UpdateVersionPayload = z.infer<typeof UpdateVersionPayloadSchema>;
